/**
 * Boombox admin pages — the ONE list behind the sidebar's Settings group
 * (`compositions/Sidebar.tsx`) and the ⌘K palette's SETTINGS section
 * (`agent-config.tsx`), so a new admin page is searchable the day it ships.
 * Both are shown only to `comms` app admins. `keywords` holds the words an
 * admin would type for a page whose label does not say them. Kept in
 * alphabetical order by label, like HQ's Settings list.
 */

import {
  AtSign,
  ClipboardList,
  Contact,
  Download,
  Inbox,
  type LucideIcon,
  MailCheck,
  Plug,
  Sparkles,
} from 'lucide-react'

import { BOOMBOX_ROUTES, type BoomboxRoute } from './routes'

export interface SettingsNavEntry {
  key: string
  label: string
  href: BoomboxRoute
  icon: LucideIcon
  keywords: string
}

export const SETTINGS_NAV: SettingsNavEntry[] = [
  {
    key: 'ai',
    label: 'AI',
    href: BOOMBOX_ROUTES.ADMIN_AI,
    icon: Sparkles,
    keywords: 'prompt drafter broadcast model',
  },
  {
    key: 'audits',
    label: 'Audits',
    href: BOOMBOX_ROUTES.ADMIN_AUDITS,
    icon: ClipboardList,
    keywords: 'audit log webhooks history',
  },
  {
    // Preview-only page: the backing /api/captured-comms endpoint 404s
    // outside preview deployments, so the page is empty there. Left in the
    // admin list unconditionally for v0; if it becomes noise in prod, add a
    // `previewOnly` field and filter it here.
    key: 'captured-comms',
    label: 'Captured comms',
    href: BOOMBOX_ROUTES.ADMIN_CAPTURED_COMMS,
    icon: Inbox,
    keywords: 'preview mailpit sandbox',
  },
  {
    key: 'imports',
    label: 'Imports',
    href: BOOMBOX_ROUTES.ADMIN_IMPORTS,
    icon: Download,
    keywords: 'customer.io customerio migrate migration',
  },
  {
    key: 'inbound',
    label: 'Inbound',
    href: BOOMBOX_ROUTES.ADMIN_INBOUND,
    icon: Inbox,
    keywords: 'receive incoming reply sms mms messages forwarding phone',
  },
  {
    key: 'integrations',
    label: 'Integrations',
    href: BOOMBOX_ROUTES.ADMIN_INTEGRATIONS,
    icon: Plug,
    keywords: 'sendgrid twilio front customer.io customerio providers api keys credentials',
  },
  {
    key: 'senders',
    label: 'Senders',
    href: BOOMBOX_ROUTES.ADMIN_SENDERS,
    icon: AtSign,
    keywords: 'from address reply-to verified sendgrid email identity',
  },
  {
    key: 'team',
    label: 'Team',
    href: BOOMBOX_ROUTES.ADMIN_TEAM,
    icon: Contact,
    keywords: 'team staff roster hq access',
  },
  {
    key: 'topics',
    label: 'Topics',
    href: BOOMBOX_ROUTES.ADMIN_TOPICS,
    icon: MailCheck,
    keywords: 'subscription topics unsubscribe preferences opt out',
  },
]
